/**
 * The ONE telemetry URL policy (REF-6708 privacy slice B), as dependency-free ES5 so the same code can
 * later be emitted into a synchronous head script (slice B2) instead of being hand-duplicated there.
 * `telemetryUrl.ts` wraps it with types and the rules derived from `PATHS`; import that, not this.
 *
 * TELEMETRY COPIES ONLY. Never sanitise a URL that is used for a request, a navigation, a download or
 * a signature — the output is deliberately lossy.
 *
 * Rules (spec §2):
 *   P0 parse structurally against no origin: absolute http(s)/ws(s), protocol-relative, root-relative,
 *      API-relative; split `?` / `#` before decoding; decode a segment only to classify it.
 *   P1 app path secrets: a segment in the position of a secret route param becomes `[redacted]`.
 *   P2 API path secrets: the segment after a listed API prefix becomes `[redacted]`, anywhere in the path.
 *   P3 query: a value survives only when its key has a validator AND the decoded value passes it.
 *   P4 fragment dropped. P5 userinfo dropped. P6 opaque schemes / parse failure → `[redacted]`.
 *   P7 free text: URLs, root-relative paths, API-relative paths, standalone `?…` / `#…` found and sanitised.
 *      A `data:` / `blob:` URL is ONE opaque match (H3): the whole run up to the next stop character becomes
 *      `[redacted]` — no payload, MIME parameter, blob origin or blob id survives (URL mode already fails closed).
 *   P8 structured walk: copies, never mutates; cycles, depth > 6 and non-plain objects fail closed.
 * A route-template segment (`:token`) is never redacted, so transaction names stay readable and the
 * functions are idempotent.
 */

/**
 * @param {{
 *   appRules: Array<Array<{ literal?: string, secret?: boolean }>>,
 *   apiPrefixes: string[][],
 *   queryValidators: Object<string, { kind: "id" } | { kind: "enum", values: string[] } | { kind: "attribution" }>,
 *   urlKeys: string[]
 * }} config
 */
export function createTelemetryUrl(config) {
  var REDACTED = "[redacted]";
  var MAX_DEPTH = 6;
  var VALID_KEY = /^[A-Za-z0-9_.-]{1,40}$/;
  var ABSOLUTE = /^([A-Za-z][A-Za-z0-9+.-]*:)?\/\//;
  var SCHEME = /^([A-Za-z][A-Za-z0-9+.-]*):/;
  var AUTHORITY = /^(\[[0-9A-Fa-f:.]+\]|[A-Za-z0-9._~%!$&'()*+,;=-]*)(?::(\d{0,5}))?$/;

  function validAuthority(authority) {
    var match = AUTHORITY.exec(authority);
    if (!match) return false;
    if (match[2] && Number(match[2]) > 65535) return false;
    var host = match[1];
    if (host.charAt(0) !== "[") return true;
    // IPv6: at most one "::", never ":::", 1–8 groups of 1–4 hex digits (an IPv4 tail counts as two).
    var inner = host.slice(1, -1);
    if (inner.indexOf(":::") !== -1 || inner.split("::").length > 2 || inner === "") return false;
    var groups = inner.split(":");
    var count = 0;
    for (var g = 0; g < groups.length; g++) {
      if (groups[g] === "") continue;
      if (g === groups.length - 1 && /^\d{1,3}(\.\d{1,3}){3}$/.test(groups[g])) count += 2;
      else if (/^[0-9A-Fa-f]{1,4}$/.test(groups[g])) count += 1;
      else return false;
    }

    return inner.indexOf("::") !== -1 ? count < 8 : count === 8;
  }
  var NETWORK_SCHEMES = { http: true, https: true, ws: true, wss: true };
  var appRules = config.appRules;
  var apiPrefixes = config.apiPrefixes;
  var queryValidators = config.queryValidators;
  var UUID_VALUE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Validators are DATA (so the head bootstrap can carry them as JSON); this is their one evaluator.
  // An unknown kind fails closed.
  function passes(validator, value) {
    if (validator.kind === "id") return UUID_VALUE.test(value) || /^\d{1,12}$/.test(value);
    if (validator.kind === "enum") {
      for (var v = 0; v < validator.values.length; v++) if (validator.values[v] === value) return true;

      return false;
    }
    if (validator.kind === "attribution") {
      // An explicit retention exception for ad attribution, NOT a credential detector: an opaque token
      // deliberately put under utm_*/gclid survives. No `:`, `/`, `@`, `%`, `=`, and nothing JWT-shaped.
      return /^[A-Za-z0-9._~-]{1,256}$/.test(value) && !/^eyJ/.test(value) && !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value);
    }

    return false;
  }
  var urlKeys = {};
  for (var k = 0; k < config.urlKeys.length; k++) urlKeys[config.urlKeys[k]] = true;

  function decode(text) {
    try {
      return decodeURIComponent(text.replace(/\+/g, " "));
    } catch (e) {
      return null;
    }
  }

  function segmentKey(segment) {
    var decoded = decode(segment);

    return decoded === null ? null : decoded.toLowerCase();
  }

  // Only an exact route-template identifier (`:token`, `:productId?`) is kept as written — not any
  // segment that merely starts with ":" (`:X.Y` is a value the router would accept as the token).
  var TEMPLATE = /^:[A-Za-z_][A-Za-z0-9_]*\??$/;

  function isTemplateOrRedacted(segment) {
    return segment === REDACTED || TEMPLATE.test(segment);
  }

  // P1 — rules are anchored at the start of the path. `segments[0]` is "" for a root-relative path.
  function applyAppRules(segments) {
    if (segments.length < 2 || segments[0] !== "") return;
    for (var r = 0; r < appRules.length; r++) {
      var rule = appRules[r];
      var matched = true;
      for (var i = 0; i < rule.length && matched; i++) {
        var segment = segments[i + 1];
        if (rule[i].literal !== undefined) {
          matched = segment !== undefined && segmentKey(segment) === rule[i].literal;
        }
      }
      if (!matched) continue;
      for (var j = 0; j < rule.length; j++) {
        var target = segments[j + 1];
        if (rule[j].secret && target !== undefined && target !== "" && !isTemplateOrRedacted(target)) {
          segments[j + 1] = REDACTED;
        }
      }
    }
  }

  // P2 — matched anywhere, so `/v1/teams/accept_invite/<t>` and a bare `teams/accept_invite/<t>` agree.
  function applyApiPrefixes(segments) {
    for (var p = 0; p < apiPrefixes.length; p++) {
      var prefix = apiPrefixes[p];
      for (var start = 0; start + prefix.length < segments.length; start++) {
        var matched = true;
        for (var i = 0; i < prefix.length && matched; i++) {
          matched = segmentKey(segments[start + i]) === prefix[i];
        }
        var target = segments[start + prefix.length];
        if (matched && target !== "" && !isTemplateOrRedacted(target)) segments[start + prefix.length] = REDACTED;
      }
    }
  }

  // Resolve `.` / `..` and collapse empty inner segments BEFORE classifying, the way the router and the
  // server read the path: `/a/../reset-password/T` and `/reset-password//T` are the secret route.
  function normalizeSegments(segments) {
    var out = [];
    var last = segments.length - 1;
    for (var i = 0; i < segments.length; i++) {
      var segment = segments[i];
      var key = segmentKey(segment);
      if (segment === "" && i !== 0 && i !== last) continue;
      if (key === ".") continue;
      if (key === "..") {
        if (out.length > 1 || (out.length === 1 && out[0] !== "")) out.pop();
        continue;
      }
      out.push(segment);
    }

    return out;
  }

  function sanitizePath(path) {
    if (path === "") return path;
    var segments = normalizeSegments(path.split("/"));
    applyAppRules(segments);
    applyApiPrefixes(segments);

    return segments.join("/");
  }

  // P3 — fail closed: only a validated value under a listed key survives. `&` and `;` both separate.
  function sanitizeQuery(query) {
    if (query === "") return query;
    var out = "";
    var parts = query.split(/([&;])/);
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (part === "&" || part === ";" || part === "") {
        out += part;
        continue;
      }
      var eq = part.indexOf("=");
      var key = eq === -1 ? part : part.slice(0, eq);
      if (!VALID_KEY.test(key)) {
        out += REDACTED + "=" + REDACTED;
        continue;
      }
      if (eq === -1) {
        out += key;
        continue;
      }
      var raw = part.slice(eq + 1);
      if (raw === "") {
        out += key + "=";
        continue;
      }
      var validator = Object.prototype.hasOwnProperty.call(queryValidators, key) ? queryValidators[key] : null;
      var decoded = decode(raw);
      out += key + "=" + (validator && decoded !== null && passes(validator, decoded) ? raw : REDACTED);
    }

    return out;
  }

  /** Full or relative URL in, same form out. Never throws, never returns the input on failure. */
  function sanitizeUrl(input) {
    try {
      if (typeof input !== "string") return REDACTED;
      // As the browser does: tab / LF / CR are removed anywhere, leading and trailing C0 + space trimmed.
      // Any other control character left fails closed.
      // Index loops, not a `[\x00-\x20]+$` regex: that one is quadratic on a long run of spaces / NULs.
      var text = input.replace(/[\t\n\r]/g, "");
      var start = 0;
      var end = text.length;
      while (start < end && text.charCodeAt(start) <= 0x20) start++;
      while (end > start && text.charCodeAt(end - 1) <= 0x20) end--;
      text = text.slice(start, end);
      // eslint-disable-next-line no-control-regex -- matching control characters is the point (R2)
      if (/[\x00-\x1f\x7f]/.test(text)) return REDACTED;
      var hash = text.indexOf("#");
      if (hash !== -1) text = text.slice(0, hash); // P4
      var origin = "";
      // Browsers read "\\" as "/" in http(s)/ws(s) URLs; every other scheme is redacted below, so read it
      // that way everywhere (in a query value it only makes a validator fail, i.e. redact).
      text = text.split("\\").join("/");
      var absolute = ABSOLUTE.exec(text);
      if (absolute) {
        if (absolute[1] && !NETWORK_SCHEMES[absolute[1].slice(0, -1).toLowerCase()]) return REDACTED;
        var authorityStart = absolute[0].length;
        // After a network scheme the browser skips any extra slashes: `https:////x/…` is host `x`.
        if (absolute[1]) while (text.charAt(authorityStart) === "/") authorityStart++;
        var authorityEnd = text.slice(authorityStart).search(/[/?]/);
        authorityEnd = authorityEnd === -1 ? text.length : authorityStart + authorityEnd;
        var authority = text.slice(authorityStart, authorityEnd);
        var at = authority.lastIndexOf("@");
        if (at !== -1) authority = authority.slice(at + 1); // P5
        if (!validAuthority(authority)) return REDACTED;
        origin = absolute[0] + authority;
        text = text.slice(authorityEnd);
      } else if (SCHEME.test(text)) {
        return REDACTED; // P6 — data:, blob:, javascript:, mailto:, a bare host:port, … all fail closed
      }
      var question = text.indexOf("?");
      var path = question === -1 ? text : text.slice(0, question);
      var query = question === -1 ? null : text.slice(question + 1);

      return origin + sanitizePath(path) + (query === null ? "" : "?" + sanitizeQuery(query));
    } catch (e) {
      return REDACTED;
    }
  }

  /** Path (+ query) only: an absolute URL loses its origin. For tags, fingerprints and names. */
  function sanitizeRoute(input) {
    var clean = sanitizeUrl(input);
    var absolute = ABSOLUTE.exec(clean);
    if (!absolute) return clean;
    var rest = clean.slice(absolute[0].length);
    var pathStart = rest.search(/[/?]/);

    return pathStart === -1 ? "/" : (rest.charAt(pathStart) === "?" ? "/" : "") + rest.slice(pathStart);
  }

  var TEXT_STOP = "\\s\"'`<>";
  var TEXT_BOUNDARY = "(^|[\\s\"'`(<>=,:\\[{])";
  var apiAlternatives = [];
  for (var a = 0; a < apiPrefixes.length; a++) apiAlternatives.push(apiPrefixes[a].join("\\/"));
  var STOP_RUN = "[^" + TEXT_STOP + "]";
  var HOST_PREFIX = "(?:[A-Za-z0-9-]+\\.)+[A-Za-z]{2,}(?::\\d{1,5})?\\/";
  var TEXT_PATTERN = new RegExp(
    [
      "((?:https?|wss?):\\/\\/" + STOP_RUN + "+)",
      TEXT_BOUNDARY +
        "(" +
        [
          "\\/" + STOP_RUN + "*",
          "\\?" + STOP_RUN + "+",
          "#" + STOP_RUN + "*",
          // a schemeless host: `app.refabric.com/reset-password/T`, `api.x:8443/v1/…`
          HOST_PREFIX + STOP_RUN + "*",
          "(?:" + apiAlternatives.join("|") + ")\\/" + STOP_RUN + "*",
        ].join("|") +
        ")",
    ].join("|"),
    "gi"
  );
  var HOST_LED = new RegExp("^" + HOST_PREFIX);
  // H3 — a `data:` / `blob:` URL is ONE opaque match, removed in a pass BEFORE the URL / path pass so no other
  // branch (a root-relative path, the network-URL branch inside `blob:https://…`) can keep part of it.
  //   1. `data:` / `blob:` after the start or ANY character that is not an ASCII letter — punctuation, digits,
  //      unicode dashes / quotes, whitespace — its own boundary (a consuming char: no lookbehind, which older
  //      Safari cannot parse and would break the head bootstrap), so `metadata:` / `userdata:` / `database:`
  //      stay prose; at least one character after the colon, so the prose word `data:` + space is left alone;
  //   2. `blob:` + `http(s)://` or `null/` wherever it appears (`xblob:https://…` included).
  var OPAQUE_TEXT = new RegExp("(^|[^A-Za-z])((?:data|blob):" + STOP_RUN + "+)|(blob:(?:https?:\\/\\/|null\\/)" + STOP_RUN + "+)", "gi");

  // P7 — one trailing `.`, `,`, `;` or `)` before the terminator is prose, not URL.
  function sanitizeMatch(match) {
    var tail = "";
    if (match.length > 1 && /[.,;)]$/.test(match)) {
      tail = match.slice(-1);
      match = match.slice(0, -1);
    }
    if (match.charAt(0) === "#") return tail;
    if (match.charAt(0) === "?") return "?" + sanitizeQuery(match.slice(1)) + tail;
    // A schemeless host is read as protocol-relative, so the path rules see `/reset-password/…`.
    if (HOST_LED.test(match)) {
      var clean = sanitizeUrl("//" + match);

      return (clean.slice(0, 2) === "//" ? clean.slice(2) : clean) + tail; // `[redacted]` stays whole
    }

    return sanitizeUrl(match) + tail;
  }

  /** Free text that may EMBED urls / paths. Not a general secret detector. */
  function sanitizeUrlsInText(text) {
    if (typeof text !== "string") return text;
    try {
      var withoutOpaque = text.replace(OPAQUE_TEXT, function (whole, before, opaque) {
        return opaque ? before + REDACTED : REDACTED;
      });

      return withoutOpaque.replace(TEXT_PATTERN, function (whole, absolute, boundary, relative) {
        if (absolute) return sanitizeMatch(absolute);

        return boundary + sanitizeMatch(relative);
      });
    } catch (e) {
      return REDACTED;
    }
  }

  function isPlainObject(value) {
    if (Object.prototype.toString.call(value) !== "[object Object]") return false;
    var proto = Object.getPrototypeOf(value);

    return proto === Object.prototype || proto === null;
  }

  function walk(value, key, depth, ancestors) {
    if (depth > MAX_DEPTH && value !== null && value !== undefined) return "[redacted:depth]";
    if (typeof value === "string") return key !== null && urlKeys[key] ? sanitizeUrl(value) : sanitizeUrlsInText(value);
    if (value === null || typeof value !== "object") {
      return typeof value === "function" || typeof value === "symbol" ? "[redacted:object]" : value;
    }
    for (var i = 0; i < ancestors.length; i++) if (ancestors[i] === value) return "[redacted:cycle]";
    var isArray = Object.prototype.toString.call(value) === "[object Array]";
    if (!isArray && !isPlainObject(value)) return "[redacted:object]";
    ancestors.push(value);
    var copy;
    if (isArray) {
      copy = [];
      for (var j = 0; j < value.length; j++) copy.push(walk(value[j], key, depth + 1, ancestors));
    } else {
      copy = {};
      for (var name in value) {
        if (Object.prototype.hasOwnProperty.call(value, name)) copy[name] = walk(value[name], name, depth + 1, ancestors);
      }
    }
    ancestors.pop();

    return copy;
  }

  /** Copy of a JSON-ish value with every string sanitised; never mutates the input. */
  function sanitizeDeep(value) {
    try {
      return walk(value, null, 0, []);
    } catch (e) {
      return REDACTED;
    }
  }

  return {
    REDACTED: REDACTED,
    sanitizeUrl: sanitizeUrl,
    sanitizeRoute: sanitizeRoute,
    sanitizeQuery: sanitizeQuery,
    sanitizeUrlsInText: sanitizeUrlsInText,
    sanitizeDeep: sanitizeDeep,
  };
}
